import { useCallback, useRef } from 'react';

import { tap } from 'rxjs/operators/tap';

import { useObservableEffect } from '@atlassian/rx-hooks/use-observable-effect';
import { useStatefulObservableLazyEffect } from '@atlassian/rx-hooks/use-stateful-observable-lazy-effect';

import {
	type ConvoAIErrorMessage,
	type ConvoAIStreamingRequest,
} from '../api/ConvoAI/ConvoAIStreamingModels';
import type {
	AnswerPartResponseMessage,
	ChannelIdResponseMessage,
	ErrorResponseMessage,
	FinalResponseMessage,
	FollowUpObject,
	FollowUpResponseMessage,
} from '../api/ConvoAI/ConvoAIStreamMessages';
import { convoAIStreamingEffect } from '../effects/convoAIStreamingEffect';
import { type UserConvoAIStreamingConfig } from '../models/ConvoAIStreamingConfig';
import {
	type ConvoAIStreamingState,
	getDefaultConvoAIStreamingState,
} from '../models/ConvoAIStreamingState';

export function useConvoAiStreamingLazy<
	TConvoAIStreamingRequest extends ConvoAIStreamingRequest,
	TFinalResponse extends FinalResponseMessage = FinalResponseMessage<string>,
	TError extends ErrorResponseMessage = ErrorResponseMessage<`${ConvoAIErrorMessage}`>,
	TFollowUp extends FollowUpResponseMessage = FollowUpResponseMessage<FollowUpObject[]>,
	TAnswerPart extends AnswerPartResponseMessage = TFinalResponse,
	TChannelId extends ChannelIdResponseMessage = TFinalResponse,
>(
	config: UserConvoAIStreamingConfig,
	onStateChange?: (
		state: ConvoAIStreamingState<TFinalResponse, TError, TFollowUp, TAnswerPart, TChannelId>,
	) => void,
): [
	ConvoAIStreamingState<TFinalResponse, TError, TFollowUp, TAnswerPart, TChannelId>,
	(request: TConvoAIStreamingRequest) => void,
] {
	const refetchId = useRef<string>(crypto.randomUUID());

	const [state, setState] = useStatefulObservableLazyEffect<
		ConvoAIStreamingState<TFinalResponse, TError, TFollowUp, TAnswerPart, TChannelId>,
		[TConvoAIStreamingRequest, UserConvoAIStreamingConfig, string]
	>(convoAIStreamingEffect, getDefaultConvoAIStreamingState());

	useObservableEffect(
		(input$) =>
			input$.pipe(tap(([nextState, onStateChangeCalback]) => onStateChangeCalback?.(nextState))),
		[state, onStateChange],
	);

	const fetch = useCallback(
		(request: TConvoAIStreamingRequest) => {
			refetchId.current = crypto.randomUUID();
			setState({
				inputs: [request, config, refetchId.current],
				state: getDefaultConvoAIStreamingState(),
			});
		},
		[config, setState],
	);

	return [state, fetch];
}
